import React from 'react';
import {
    HStack,
    TabList,
    TabPanel,
    Tabs,
    VStack,
    Box,
    Button,
    Text,
    Tab,
    DataList,
    DataItem,
} from '@cad-arc';
import { observer } from 'mobx-react-lite';
import { TabSubtitle } from '../../../panels/entity-panels/tabs/tab-subtitle';
import { usePhoneCallsStore } from '../../../phone-calls/use-phone-calls';
import { PhoneCallInfoDisplay } from './phone-call-info-display';
import { useEventMetadataStore } from '../../event-panel-form/event-metadata';
import { format } from 'date-fns';
import type { AmlCallView, PhoneCallView } from '../../../phone-calls/types';
import { partition, startCase } from 'lodash';
import { ErrorBoundary } from '../../../monitoring/error-boundary';
import { useCallImport } from './use-call-import';
import { usePhoneCallForEvent } from './get-phone-call-for-event';
import { useApiClient } from '~/api-client';
import { formatMonthDayYearHoursMinutes } from '../../../common/helpers/utils';
import { electronBridge } from '../../../electron-bridge/electron-bridge';
import { useEnabledFeatures } from '../../../common/hooks/enabled-features';
import { RapidsosResource } from '@mark43/cad-api';

export function getAmlCallForEvent(
    amlCalls: AmlCallView[],
    phoneCallSelection: PhoneCallView | undefined
): AmlCallView | undefined {
    // if the event is selecting a phone call,  use the call from form state
    if (phoneCallSelection) {
        return amlCalls.find(
            (amlCall) => amlCall.phoneNumberId === phoneCallSelection.phoneNumberId
        );
    }
    return undefined;
}

type RapidSosAdditionalData = Record<string, string | Record<string, unknown>>;

function formatValue(key: string, value: string | number) {
    switch (key) {
        case 'date_of_birth':
        case 'last_updated':
        case 'timestamp':
            return formatMonthDayYearHoursMinutes(
                new Date(typeof value === 'string' ? parseInt(value) : value).toISOString()
            );
        default:
            return value;
    }
}

function prettifyTitle(str: string): string {
    return startCase(str);
}

function RapidSosAdditionalDataSection(props: {
    nestingLevel: number;
    title?: string;
    data: RapidSosAdditionalData;
}): React.ReactElement {
    if (!props.data) return <></>;
    const entries = Object.entries(props.data);
    const [nonPrimitives, primitives] = partition(entries, (entry) => {
        return typeof entry[1] === 'object';
    });
    return (
        <VStack
            // pl={`${props.nestingLevel * 8}px`}
            mt={props.nestingLevel === 0 ? 3 : undefined}
            alignItems="flex-start"
        >
            {props.title ? (
                <Box
                    w="100%"
                    borderBottomWidth={props.nestingLevel === 0 ? undefined : '1px'}
                    borderBottomColor="border.default"
                >
                    <Text
                        variant={props.nestingLevel === 0 ? 'headingXs' : 'caption'}
                        color="text.placeholder"
                    >
                        {prettifyTitle(props.title)}
                    </Text>
                </Box>
            ) : undefined}
            <VStack alignItems="flex-start" pl={`${(props.nestingLevel + 1) * 8}px`}>
                <DataList>
                    {(primitives as [string, string][]).map(([key, val]) => (
                        <DataItem key={key} dataKey={prettifyTitle(key)}>
                            {formatValue(
                                key,
                                typeof val === 'number' ? (val as number).toString() : val
                            )}
                        </DataItem>
                    ))}
                </DataList>
                {nonPrimitives.length > 0
                    ? (nonPrimitives as [string, Record<string, unknown>][]).map(([key, val]) =>
                          Array.isArray(val) ? (
                              <>
                                  <Box
                                      w="100%"
                                      borderBottomWidth={
                                          props.nestingLevel === 0 ? undefined : '1px'
                                      }
                                      borderBottomColor="border.default"
                                  >
                                      <Text
                                          variant={
                                              props.nestingLevel === 0 ? 'headingXs' : 'caption'
                                          }
                                          color="text.placeholder"
                                      >
                                          {prettifyTitle(key)}
                                      </Text>
                                  </Box>
                                  {val.map((value) => (
                                      <RapidSosAdditionalDataSection
                                          key={key}
                                          nestingLevel={props.nestingLevel + 1}
                                          data={value && value.data ? value.data : value}
                                      />
                                  ))}
                              </>
                          ) : (
                              <RapidSosAdditionalDataSection
                                  key={key}
                                  nestingLevel={props.nestingLevel + 1}
                                  title={key}
                                  // @ts-expect-error unknowable type
                                  data={val && val.data ? val.data : val}
                              />
                          )
                      )
                    : undefined}
            </VStack>
        </VStack>
    );
}

const RapidSosAdditionalInformation = React.memo(function RapidSosAdditionalInformation(props: {
    additionalData?: string;
}): React.ReactElement {
    const parsedData = React.useMemo(() => {
        return props.additionalData ? JSON.parse(props.additionalData) : undefined;
    }, [props.additionalData]);
    const topLevelKeys = Object.keys(parsedData);
    if (!parsedData || topLevelKeys.length === 0) {
        return <></>;
    }
    return (
        <>
            {topLevelKeys.map((key) => (
                <RapidSosAdditionalDataSection
                    key={key}
                    nestingLevel={0}
                    title={prettifyTitle(key)}
                    data={parsedData[key].data ? parsedData[key].data : {}}
                />
            ))}
        </>
    );
});

export const RapidSosSection = observer(function RapidSosSection(): React.ReactElement | null {
    const apiClient = useApiClient();
    const amlCalls = usePhoneCallsStore().amlCallsList;
    const phoneCallForEvent = usePhoneCallForEvent();
    const eventMetadata = useEventMetadataStore().state.metadata;
    const eventExists = !!eventMetadata?.agencyEventNumber;
    const amlCall = getAmlCallForEvent(amlCalls, phoneCallForEvent);
    const callImport = useCallImport(eventExists);
    const enabledFeatures = useEnabledFeatures();
    if (!amlCall || !phoneCallForEvent) {
        return null;
    }
    const lastUpdated = amlCall.locations[0].createdDateUtc;
    return (
        <>
            <TabSubtitle
                subtitleTrailingNode={
                    <Button
                        trailingVisual="TransmitData"
                        isDisabled={eventExists}
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                            callImport.importAll(amlCall);
                        }}
                    >
                        Import All
                    </Button>
                }
                rightContent={
                    <Button
                        variant="ghost"
                        trailingVisual="OpenNewWindow"
                        onClick={() => {
                            if (enabledFeatures.cadReplacesAmlEnabled) {
                                apiClient
                                    // we dont produce types for the AML resource so have to `any` here
                                    .request<RapidsosResource.GetRapidSosPortalUrl>({
                                        method: 'GET',
                                        url: `rapidsos/portal/${amlCall.phoneNumber}/url`,
                                    })
                                    .then((response) => {
                                        const urlToOpen = response.data.data;
                                        if (urlToOpen) {
                                            if (electronBridge) {
                                                electronBridge.openUrlInBrowser(urlToOpen);
                                            } else {
                                                window.open(urlToOpen);
                                            }
                                        }
                                    });
                            } else {
                                apiClient
                                    // we dont produce types for the AML resource so have to `any` here
                                    .request<any>({
                                        method: 'GET',
                                        url: `rapidsos/portal/${amlCall.phoneNumber}/url`,
                                        service: 'AML',
                                    })
                                    .then((response) => {
                                        const urlToOpen = response.data.data;
                                        if (urlToOpen) {
                                            if (electronBridge) {
                                                electronBridge.openUrlInBrowser(urlToOpen);
                                            } else {
                                                window.open(urlToOpen);
                                            }
                                        }
                                    });
                            }
                        }}
                    >
                        RapidSOS Portal
                    </Button>
                }
            >
                RapidSOS
            </TabSubtitle>
            <Tabs defaultValue="location" style={{ width: '100%' }}>
                <HStack w="100%" justifyContent="space-between">
                    <TabList>
                        <Tab value="location" style={{ fontSize: '14px' }}>
                            Location
                        </Tab>
                        <Tab value="additional-information" style={{ fontSize: '14px' }}>
                            Additional Information
                        </Tab>
                    </TabList>
                    {lastUpdated ? (
                        <Text fontFamily="mono" fontSize="12px">
                            {`Last Updated ${format(new Date(lastUpdated), 'MM/dd HH:mm')}`}
                        </Text>
                    ) : (
                        <div></div>
                    )}
                </HStack>
                <TabPanel
                    value="location"
                    style={{
                        padding: 0,
                        paddingTop: 'var(--arc-space-2)',
                        paddingBottom: 'var(--arc-space-2)',
                    }}
                >
                    <PhoneCallInfoDisplay
                        isAmlCall
                        eventExists={eventExists}
                        selectedCall={amlCall}
                        phoneCall={phoneCallForEvent}
                    />
                </TabPanel>
                <TabPanel
                    value="additional-information"
                    style={{
                        padding: 0,
                        paddingTop: 'var(--arc-space-2)',
                        paddingBottom: 'var(--arc-space-2)',
                    }}
                >
                    <ErrorBoundary name="RAPID_SOS_ADDITIONAL_INFO">
                        <RapidSosAdditionalInformation
                            additionalData={amlCall.additionalData?.payload ?? '{}'}
                        />
                    </ErrorBoundary>
                </TabPanel>
            </Tabs>
        </>
    );
});
